import type { QueryKey } from "@tanstack/react-query";
import {
	type MutationInvalidate,
	type MutationReplayHandler,
	type MutationReplaySuccess,
	registerMutation,
} from "@/lib/mutationRegistry";
import { enqueue } from "@/lib/offlineQueue";
import type { OfflineWriteKey } from "./capabilities";
import { getSnapshot as reachabilitySnapshot } from "./reachability";

/**
 * Sentinel a write resolves with when it was queued offline instead of sent.
 * It structurally masquerades as the mutation's normal response so callsites
 * keep programming against `onSuccess`/`isPending` (an offline write is a
 * *success* from the UI's point of view — the form should close, the button
 * should re-lock). The central MutationCache detects it via `isOfflineQueued`
 * to swap the "created" toast for the "saved offline" toast.
 */
export const OFFLINE_QUEUED = { __offlineQueued: true } as const;
export type OfflineQueuedResult = typeof OFFLINE_QUEUED;

export function isOfflineQueued(data: unknown): data is OfflineQueuedResult {
	return (
		typeof data === "object" &&
		data !== null &&
		(data as { __offlineQueued?: unknown }).__offlineQueued === true
	);
}

/**
 * Thrown when the offline write could not even be persisted to the IDB queue —
 * a genuine failure the user must know about. The MutationCache maps it to the
 * `offline.mutations.enqueueFailed` toast; every other error is a real server
 * error and flows through the normal error toast.
 */
class OfflineEnqueueError extends Error {
	constructor() {
		super("Failed to persist offline mutation to the queue");
		this.name = "OfflineEnqueueError";
	}
}

export function isOfflineEnqueueError(
	error: unknown,
): error is OfflineEnqueueError {
	return error instanceof OfflineEnqueueError;
}

export type OfflineMutationConfig<TVariables, TData> = {
	networkMode: "offlineFirst";
	meta: { offlineQueueKey: OfflineWriteKey; payloadVersion: number };
	/**
	 * Drop-in replacement for the generated mutationFn. Online it is the real
	 * call; offline it enqueues the variables and resolves with the sentinel, so
	 * the mutation reaches its *success* lifecycle instead of erroring. The
	 * return type stays `TData` (the sentinel masquerades) so callsites and the
	 * Kubb base hook see one honest signature.
	 */
	mutationFn: (variables: TVariables) => Promise<TData>;
};

/**
 * Declares an offline-capable write in one place: registers the replay handler,
 * AND returns the `{ networkMode, meta, mutationFn }` config the mutation hook
 * must spread — so a partial declaration (registered but unkeyed, keyed but
 * unregistered, or queued without a matching UI-success signal) is impossible
 * by construction. Call at module scope of the api module — the registration
 * runs at module load, and `replayHandlers.ts` imports the module on replay so
 * a queued write still finds its handler after a page refresh.
 *
 * The completion contract is centralized here: the returned `mutationFn`
 * attempts the network first (`networkMode: "offlineFirst"` fires it once even
 * while unreachable) and, only on a failure taken while `reachability` says
 * we're offline, enqueues `variables` verbatim and resolves with the sentinel.
 * Callsites therefore need no offline-aware branching: their existing
 * `onSuccess` (close dialog / navigate) runs identically online and offline.
 *
 * `TVariables` is the mutation's variables object; the replay handler receives
 * exactly that shape. Bump `payloadVersion` whenever it changes.
 *
 * `invalidate` registers the success effect — the query keys to refresh after a
 * replay. The per-component `onSuccess` invalidation dies on a page refresh and
 * never runs on replay, so the registry is the path-agnostic home for it (D4).
 * Keys must be derivable from `payload` alone, not from React closure state.
 *
 * `onReplaySuccess` registers a **replay-only** success effect, for outcomes a
 * callsite reports itself online but cannot report on replay (the batch
 * commit's per-item partial failures). It never runs on the online path: the
 * `mutationFn` below calls `replay` directly, while only the queue path goes
 * through `replayMutation`, where the effect lives. Keep it to disclosure —
 * a toast, not recovery — since the queued draft is long gone by then.
 */
export function defineOfflineMutation<TVariables, TData = unknown>(opts: {
	key: OfflineWriteKey;
	payloadVersion: number;
	replay: (payload: TVariables) => Promise<TData>;
	invalidate?: (payload: TVariables) => QueryKey[];
	onReplaySuccess?: (payload: TVariables, data: TData) => void;
}): OfflineMutationConfig<TVariables, TData> {
	registerMutation(
		opts.key,
		opts.replay as MutationReplayHandler,
		opts.payloadVersion,
		opts.invalidate as MutationInvalidate | undefined,
		opts.onReplaySuccess as MutationReplaySuccess | undefined,
	);

	const mutationFn = async (variables: TVariables): Promise<TData> => {
		try {
			return await opts.replay(variables);
		} catch (err) {
			// Snapshot gate mirrors the old MutationCache.onError predicate: a
			// write is queued only when made while unreachable — a real online
			// failure (validation, 5xx) rethrows into the normal error path.
			if (reachabilitySnapshot()) throw err;
			try {
				await enqueue(opts.key, variables, opts.payloadVersion);
			} catch {
				throw new OfflineEnqueueError();
			}
			// Sentinel masquerades as TData; the MutationCache detects it
			// structurally via isOfflineQueued.
			return OFFLINE_QUEUED as unknown as TData;
		}
	};

	return {
		networkMode: "offlineFirst",
		meta: { offlineQueueKey: opts.key, payloadVersion: opts.payloadVersion },
		mutationFn,
	};
}
